/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { ajax } from 'jquery';

import { Document, DocumentUploaded } from './types';

interface OnProgress {
  (progress: number): void;
}

export default class StorageService {
  constructor(private documentableType: string, private documentableId: string, private csrfToken: string) {}

  getDocuments(): Promise<Document[]> {
    const url = `/storage/documents/list/${this.documentableType}` + `/${this.documentableId}`;

    return Promise.resolve(ajax(url, { method: 'GET' }));
  }

  uploadFile(
    file: File,
    onProgress: OnProgress | null = null,
    cancelPromise: Promise<void> | null = null
  ): Promise<DocumentUploaded> {
    const url = `/storage/documents/upload/${this.documentableType}` + `/${this.documentableId}`;

    const form = new FormData();
    form.append('file', file);

    const options: JQuery.AjaxSettings = {
      method: 'POST',
      headers: {
        'X-CSRF-Token': this.csrfToken,
      },
      data: form,
      processData: false,
      contentType: false,
    };

    if (onProgress) {
      onProgress(0);
    }

    let isAborted = false;

    options.xhr = () => {
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener(
          'progress',
          evt => {
            if (evt.lengthComputable) {
              onProgress(evt.loaded / evt.total);
            }
          },
          false
        );
      }

      if (cancelPromise) {
        cancelPromise.then(() => {
          isAborted = true;
          xhr.abort();
        });
      }

      return xhr;
    };

    return Promise.resolve(
      ajax(url, options).then(
        res => res,
        err => {
          if (isAborted) {
            return Promise.reject('aborted');
          } else {
            return Promise.reject(err);
          }
        }
      )
    );
  }

  addLink(link: string, title: string): Promise<DocumentUploaded> {
    const url = `/storage/documents/add_link/${this.documentableType}` + `/${this.documentableId}`;

    return Promise.resolve(
      ajax({
        type: 'POST',
        url: url,
        contentType: 'application/json',
        data: JSON.stringify({
          link,
          title,
        }),
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
      })
    );
  }

  deleteDocument(documentId: number): Promise<void> {
    const url = `/storage/documents/delete/${documentId}`;

    return Promise.resolve(
      ajax(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
      })
    );
  }

  renameDocument(documentId: number, newTitle: string): Promise<void> {
    const url = `/storage/documents/rename/${documentId}`;

    return Promise.resolve(
      ajax({
        type: 'POST',
        url: url,
        contentType: 'application/json',
        data: JSON.stringify({
          title: newTitle,
        }),
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
      })
    );
  }
}
